/**
 * WS-ELIG B-E3 (CORE-W:43/:45/:76) — on the technician form:
 *   - NEW entry: the person's email, so they are invited to join (required where the shop enforces real employee
 *     eligibility — a roster entry alone is not a worker there).
 *   - EXISTING entry in an enforce shop that is not operational: say so, with the reason, instead of presenting a
 *     roster name as if it were on the board.
 */
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchTechnicianWorkEligibility, WORK_ELIGIBILITY_REASON_LABEL, type TechnicianWorkEligibility } from '@/lib/roles/workEligibility';
import { newTechnicianInvitationNotice } from '@/lib/roles/rosterInvitation';

export function NewTechnicianInvitationField({ enforced, email, onEmailChange }: {
  enforced: boolean | null;
  email: string;
  onEmailChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="new-technician-invitation">
      <Label htmlFor="new-technician-invite-email">
        Their email{enforced === true ? ' *' : ''}
      </Label>
      <Input
        id="new-technician-invite-email"
        type="email"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        placeholder="name@example.com"
        data-testid="new-technician-invite-email"
      />
      <p className="text-xs text-muted-foreground" data-testid="new-technician-invitation-notice">
        {newTechnicianInvitationNotice(enforced)}
      </p>
    </div>
  );
}

export function RosterEligibilityNotice({ technicianId }: { technicianId: string | null }) {
  const [e, setE] = useState<TechnicianWorkEligibility | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!technicianId) { setE(null); return; }
    // A failed read shows nothing (unknown is not "not on the board").
    void Promise.resolve()
      .then(() => fetchTechnicianWorkEligibility(technicianId))
      .then((r) => { if (!cancelled) setE(r); }, () => { if (!cancelled) setE(null); });
    return () => { cancelled = true; };
  }, [technicianId]);
  // Only an ENFORCE shop's non-operational entry is called out: in observe the legacy roster still schedules it.
  if (!e || !e.enforced || e.operational) return null;
  const needsInvitation = e.reason === 'NO_ACCOUNT' || e.reason === 'NOT_JOINED';
  return (
    <div role="status" className="rounded-md border border-amber-500 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-50"
      data-testid="roster-not-operational-notice" data-reason={e.reason}>
      <strong>Not on the board.</strong> {WORK_ELIGIBILITY_REASON_LABEL[e.reason]}.{' '}
      {needsInvitation
        ? <>This roster entry cannot be scheduled until the person joins: invite them from Team Management (choose this
            entry under &quot;Already on your roster?&quot;) and they become schedulable once they accept.</>
        : <>This roster entry cannot be scheduled until that is resolved in Team Management.</>}
    </div>
  );
}
